import { useTranslation } from "react-i18next"
import { ArrowSquareOut, PencilSimple, Prescription } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { useCareProviderWithOrigin } from "@/modules/care-provider/services/useCareProviderWithOrigin"
import { combineCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { ChPrescriptionDrawer } from "@/modules/patients/detail/prescription/ch/ch-prescription-drawer"
import { chPrescriptionFragment$key } from "@/modules/patients/detail/prescription/ch/services/__generated__/chPrescriptionFragment.graphql"
import { DePrescriptionDrawer } from "@/modules/patients/detail/prescription/de/de-prescription-drawer"
import { dePrescriptionFragment$key } from "@/modules/patients/detail/prescription/de/services/__generated__/dePrescriptionFragment.graphql"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface PrescriptionDocumentProps extends DocumentProps {
  id?: string
  progressNoteId: string
  healthcareProviderId?: string | null
  medicationIds: readonly string[]
  prescriptionRef: chPrescriptionFragment$key | dePrescriptionFragment$key
  diagnosisCode?: string
}

const PrescriptionDocument = ({
  id,
  progressNoteId,
  healthcareProviderId,
  isEditable = true,
  medicationIds,
  prescriptionRef,
  isConclusion,
  isSigned,
  isSent,
  onSelect,
  diagnosisCode,
}: PrescriptionDocumentProps) => {
  const { t } = useTranslation()
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = isConclusion && selectedDocumentId === id
  const countryCode = useCountryParam()

  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  const careProvider = useCareProviderWithOrigin(healthcareProviderId)

  const heathcareProviderName = combineCareProviderTitle({
    businessName: careProvider?.businessName,
    title: careProvider?.title,
    firstName: careProvider?.firstName,
    lastName: careProvider?.lastName,
  })

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="prescription-document-item"
      onClick={() => {
        if (!isEditable) {
          onSelect()
          return
        }
        if (countryCode === "CH") {
          const drawerContent = diagnosisCode ? (
            <ChPrescriptionDrawer
              id={id}
              progressNoteId={progressNoteId}
              prescriptionRef={prescriptionRef as chPrescriptionFragment$key}
              diagnosisCode={diagnosisCode}
            />
          ) : (
            <div></div>
          )
          return openDrawer(() => drawerContent, diagnosisCode ? "PrescriptionDrawer" : undefined)
        }

        openDrawer(
          () => (
            <DePrescriptionDrawer
              id={id}
              progressNoteId={progressNoteId}
              prescriptionRef={prescriptionRef as dePrescriptionFragment$key}
            />
          ),
          "PrescriptionDrawer",
        )
      }}>
      <Prescription className={cn("shrink-0", isSelected ? "text-on-primary-surface" : "text-on-surface-subdued")} />
      <div className="ml-2 min-w-0">
        <ListText className={cn(isSelected && "text-on-primary-surface")}>
          {t("patient.prescription.prescription")}
        </ListText>
        <ListText
          data-testid="prescription-details"
          variant="secondary"
          className={cn("truncate", isSelected && "text-on-primary-surface")}>
          {joinStrings([heathcareProviderName, `${medicationIds.length} ${t("patient.prescription.unit.medication")}`])}
        </ListText>
      </div>
      {isEditable && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="prescription-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut className="text-on-surface-subdued" data-testid="prescription-view-document-button" />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { PrescriptionDocument }
